import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
} from '@roger/ui-components';
import type { OEType, SeverityType } from '../../types/codingData.types';

interface InlineDiagnosisFieldsProps {
  oe?: OEType;
  onsetDate?: Date | null;
  onsetDateRaw?: string | null;
  severity?: SeverityType;
  onOEChange?: (value: OEType) => void;
  onOnsetDateChange?: (value: Date | null) => void;
  onSeverityChange?: (value: SeverityType) => void;
  disabled?: boolean;
}

const NONE_VALUE = '__none__';

const OE_OPTIONS = [
  { value: NONE_VALUE, label: '—' },
  { value: 'Onset', label: 'Onset' },
  { value: 'Exacerbation', label: 'Exacerbation' },
];

const SEVERITY_OPTIONS = [
  { value: NONE_VALUE, label: '—' },
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
];

const InlineDiagnosisFields: React.FC<InlineDiagnosisFieldsProps> = ({
  oe,
  onsetDate,
  onsetDateRaw,
  severity,
  onOEChange,
  onOnsetDateChange,
  onSeverityChange,
  disabled = false,
}) => {
  const hasDateError = !!onsetDateRaw && !onsetDate;

  return (
    <div className='flex items-center gap-2 flex-wrap'>
      <div className='flex flex-col gap-0.5'>
        <label className='text-gray-700 font-medium text-[10px]'>O/E</label>
        <Select
          value={oe ?? NONE_VALUE}
          onValueChange={v =>
            onOEChange?.(v === NONE_VALUE ? null : (v as OEType))
          }
          disabled={disabled}
        >
          <SelectTrigger className='w-40'>
            <SelectValue placeholder='—' />
          </SelectTrigger>
          <SelectContent>
            {OE_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className='flex flex-col gap-0.5'>
        <label className='text-gray-700 font-medium text-[10px]'>Date</label>
        <DatePicker
          date={onsetDate ?? undefined}
          onDateChange={d => onOnsetDateChange?.(d ?? null)}
          placeholder='—'
          className='w-40'
          yearRange={{ fromYear: 1900, toYear: new Date().getFullYear() + 1 }}
          formatString='MM/dd/yyyy'
          showClearButton
          typable
          rawValue={onsetDateRaw ?? undefined}
          error={hasDateError}
        />
      </div>

      <div className='flex flex-col gap-0.5'>
        <label className='text-gray-700 font-medium text-[10px]'>
          Severity
        </label>
        <Select
          value={severity ?? NONE_VALUE}
          onValueChange={v =>
            onSeverityChange?.(v === NONE_VALUE ? null : (v as SeverityType))
          }
          disabled={disabled}
        >
          <SelectTrigger className='w-20'>
            <SelectValue placeholder='—' />
          </SelectTrigger>
          <SelectContent>
            {SEVERITY_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default InlineDiagnosisFields;
